import { useEffect, useMemo } from 'react';

import { NodeSearchOption } from './NodeSearchField';
import { useGetDispatchDestinationSuggestions } from '../../../../../queries/structure-query';

export interface UseDestinationSuggestionsArgs {
  serviceId: number;
  mainLocationNodeId?: number;
  subLocationNodeId?: number;
  primaryAllocationNodeId?: number;
  secondaryAllocationNodeId?: number;
}

// The BFF builds address strings by template-joining attribute fields, so a
// missing line renders as the literal token 'undefined'/'null'. Strip those
// before display; collapse to nothing when no real content remains.
const cleanAddress = (address?: string): string | undefined => {
  if (typeof address !== 'string') return undefined;
  const cleaned = address
    .replace(/\b(?:undefined|null)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || undefined;
};

/**
 * Curated dispatch-destination suggestions for a job — the assigned technician
 * and their parent depot (section 'Structure') plus the job's main/sub
 * location (section 'Service') — fetched from the BFF and mapped into
 * NodeSearchField options. Mirrors the standalone dispatch flow
 * (DispatchRoutePanel) so the job-scoped create form offers the same list when
 * the user opens the Destination field.
 */
export const useDestinationSuggestions = ({
  serviceId,
  mainLocationNodeId,
  subLocationNodeId,
  primaryAllocationNodeId,
  secondaryAllocationNodeId,
}: UseDestinationSuggestionsArgs): NodeSearchOption[] => {
  const mainLocation = mainLocationNodeId ?? 0;
  const subLocation = subLocationNodeId ?? 0;
  const primaryAllocation = primaryAllocationNodeId ?? 0;
  const secondaryAllocation = secondaryAllocationNodeId ?? 0;

  const query = useGetDispatchDestinationSuggestions(
    serviceId,
    mainLocation,
    subLocation,
    primaryAllocation,
    secondaryAllocation
  );
  const { refetch } = query;

  // The shared hook is enabled:false (same pattern as DispatchRoutePanel) —
  // fetch when the create form mounts, and again whenever any node ID changes
  // (each combination is its own cache entry, so a changed ID — e.g. the job
  // re-allocated to another technician — starts empty until refetched).
  // `refetch` is referentially stable in React Query v5, so it stays out of
  // the deps to avoid mechanical refetch-effect churn.
  useEffect(() => {
    if (serviceId) refetch();
  }, [serviceId, mainLocation, subLocation, primaryAllocation, secondaryAllocation]);

  return useMemo(() => {
    const seen = new Set<number>();
    return (query.data ?? []).flatMap((suggestion) => {
      // The same node can appear twice (e.g. primary and secondary allocation
      // resolve to one technician) — keep the first occurrence only.
      if (seen.has(suggestion.id)) return [];
      seen.add(suggestion.id);
      return [
        {
          id: suggestion.id,
          name: suggestion.name,
          nodeTypeName: suggestion.nodeTypeName,
          section: suggestion.section,
          mainAddress: cleanAddress(suggestion.mainAddress),
          logisticsAddress: cleanAddress(suggestion.logisticsAddress),
          serviceAddress: cleanAddress(suggestion.serviceAddress),
        } as NodeSearchOption,
      ];
    });
  }, [query.data]);
};
